import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { AuthResponseDTO, Environments, QueryParams1101DTO, SexeCode, Simulation } from "@mra/hlp9002";
import { jwtDecode } from "jwt-decode";
import { catchError, map, Observable, of, tap } from "rxjs";
import { prestationStore } from "src/app/state/prestation.store";
import { questionnaireStore } from "src/app/state/questionnaire.store";
import { renteStore } from "src/app/state/rente.store";
import { UserState } from "src/app/state/state";
import { userStore } from "src/app/state/user.store";
import { versementStore } from "src/app/state/versement.store";
import { environment } from "src/environments/environment";
import { CloudwatchService } from "../cloudwatch.service";
import { LocalStorageService, StateKey } from "../local-storage.service";
import { SimulationService } from "./simulation.service";
import { TokenService } from "./token.service";

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly tokenService = inject(TokenService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly simuService = inject(SimulationService);
  private readonly userStore = inject(userStore);
  private readonly prestationStore = inject(prestationStore);
  private readonly questionnaireStore = inject(questionnaireStore);
  private readonly renteStore = inject(renteStore);
  private readonly versementStore = inject(versementStore);
  private readonly http = inject(HttpClient);

  /**
   * enregistre une simulation pour les parcours
   * @param simu simulation
   */
  selectSimulation(simu: Simulation): void {
    this.localStorageService.isStateTracked.set(false);
    const savedState = this.localStorageService.getSavedState<UserState>(StateKey.USER);
    this.tokenService.setSelectedSimuCode(simu.code);

    if (savedState?.simulation?.code !== simu?.code) {
      this.unsetSimulation();
      this.userStore.saveSimulation(simu, true);
      this.renteStore.setBeneficiaire({
        civilite: simu.affilie.sexe.code === SexeCode.F ? SexeCode.H : SexeCode.F,
        nomPrenom: "",
        dateNaissance: simu.affilie.naissance.date
      });
    }

    this.userStore.saveUserInited(true);
    this.localStorageService.isStateTracked.set(true);
  }

  unsetSimulation(): void {
    this.prestationStore.unsetSimulation();
    this.questionnaireStore.unsetSimulation();
    this.renteStore.unsetSimulation();
    this.userStore.unsetSimulation();
    this.versementStore.unsetSimulation();
  }

  /**
   * récupère la simulation encodée dans le token
   * @param token token jwt
   * @returns simulation
   */
  retrieveUserFromToken(token: string): Observable<Simulation> {
    const datas: any = jwtDecode(token);
    const simu: Simulation = JSON.parse(datas.metadata);
    return this.simuService.getSimulationByCode(simu.code);
  }

  /**
   * authentifie l'utilisateur en fonction du token spa
   * @returns simulation
   */
  getAuth(): Observable<AuthResponseDTO> {
    const codeSimu: string = this.tokenService.getSelectedSimuCode();
    const msParams: QueryParams1101DTO = {
      tokenSpa: this.getSpaToken(),
      debugSimulation: codeSimu && codeSimu !== "" ? codeSimu : "art83_cap_001"
    };
    msParams.session = CloudwatchService.getSession();
    return this.http.post<{ data: AuthResponseDTO }>(environment.urlsMs.MS1101, msParams).pipe(
      map((authResponse) => authResponse.data),
      catchError((e) => {
        console.error(e);
        return of(null);
      }),
      tap((authResponse) => {
        this.tokenService.setToken(authResponse?.tokenAuth);
      })
    );
  }

  /**
   * si token non fourni, récupère celui du local storage. Sinon, l'enregistre dans le local storage et écrase l'éventuel token déjà stocké
   * @returns token
   */
  getSpaToken(): string {
    const params: URLSearchParams = new URLSearchParams(document.location.search);
    let token: string = params.get("tokenSpa");
    if (environment.type === Environments.P) return token;
    if (token) {
      this.localStorageService.saveSpaToken(token);
    } else {
      token = this.localStorageService.getSpaToken();
    }
    return token;
  }
}
